// src/pages/documents/FieldMappingList.tsx

import React, { useMemo } from 'react';
import { Typography, Progress, Space, Tag } from 'antd';
import styled from 'styled-components';
import { PdfFieldMapping } from '../../types';
import FieldMappingCard from './FieldMappingCard';

const { Title, Text } = Typography;

// Styled Components
const ListContainer = styled.div`
  margin-top: 24px;
`;

const PageSection = styled.div`
  margin-bottom: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 2px solid #E5E7EB;
  margin-bottom: 16px;
`;

const PageTitle = styled(Title)`
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #374151 !important;
  margin: 0 !important;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;
`;

const SummarySection = styled.div`
  background: #F9FAFB;
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  
  .label {
    font-size: 12px;
    color: #6B7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    font-size: 24px;
    font-weight: 600;
    color: #111827;
  }
`;

const ProgressContainer = styled.div`
  min-width: 200px;
`;

interface FieldMappingListProps {
  fields: PdfFieldMapping[];
  probateDocId: string;
  onUpdate: () => void;
}

const FieldMappingList: React.FC<FieldMappingListProps> = ({ 
  fields, 
  probateDocId,
  onUpdate 
}) => {
  // Group fields by page
  const groupedByPage = useMemo(() => {
    return fields.reduce((acc, field) => {
      const page = field.pageNumber || 1;
      if (!acc[page]) acc[page] = [];
      acc[page].push(field);
      return acc;
    }, {} as Record<number, PdfFieldMapping[]>);
  }, [fields]);

  // Calculate mapping statistics
  const stats = useMemo(() => {
    const totalFields = fields.length;
    const mappedFields = fields.filter(f => f.databaseFieldName !== null).length;
    const unmappedFields = totalFields - mappedFields;
    const percentage = totalFields > 0 ? Math.round((mappedFields / totalFields) * 100) : 0;
    
    // Count by type
    const typeCount = fields.reduce((acc, field) => {
      acc[field.type] = (acc[field.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    return { totalFields, mappedFields, unmappedFields, percentage, typeCount };
  }, [fields]);

  const sortedPages = Object.keys(groupedByPage).map(Number).sort((a, b) => a - b);

  if (fields.length === 0) {
    return null;
  }

  return (
    <ListContainer>
      {/* Summary Section */}
      <SummarySection>
        <SummaryItem>
          <span className="label">Total Fields</span>
          <span className="value">{stats.totalFields}</span>
        </SummaryItem>
        <SummaryItem>
          <span className="label">Mapped</span>
          <span className="value" style={{ color: '#10B981' }}>{stats.mappedFields}</span>
        </SummaryItem>
        <SummaryItem>
          <span className="label">Unmapped</span>
          <span className="value" style={{ color: '#F59E0B' }}>{stats.unmappedFields}</span>
        </SummaryItem>
        <SummaryItem>
          <span className="label">Field Types</span>
          <Space size={4}>
            {Object.entries(stats.typeCount).map(([type, count]) => (
              <Tag key={type} color={type === 'TextField' ? 'blue' : 'default'}>
                {type}: {count}
              </Tag>
            ))}
          </Space>
        </SummaryItem>
        <ProgressContainer>
          <Text type="secondary" style={{ fontSize: 12 }}>Mapping Progress</Text>
          <Progress 
            percent={stats.percentage} 
            status={stats.percentage === 100 ? 'success' : 'active'}
            strokeColor={{
              '0%': '#F59E0B',
              '100%': '#10B981',
            }}
          />
        </ProgressContainer>
      </SummarySection>

      {/* Fields grouped by page */}
      {sortedPages.map((page) => {
        const pageFields = groupedByPage[page];
        const pageMapped = pageFields.filter(f => f.databaseFieldName !== null).length;
        
        return (
          <PageSection key={page}>
            <PageHeader>
              <PageTitle level={4}>Page {page}</PageTitle>
              <Text type="secondary">
                {pageMapped}/{pageFields.length} mapped
              </Text>
            </PageHeader>
            <FieldsGrid>
              {pageFields.map((field) => (
                <FieldMappingCard
                  key={field.uid || field.id}
                  field={field}
                  documentFields={fields}
                  probateDocId={probateDocId}
                  onUpdate={onUpdate}
                />
              ))}
            </FieldsGrid>
          </PageSection>
        );
      })}
    </ListContainer>
  );
};

export default FieldMappingList;